import { useState, useRef, useCallback, useEffect } from "react";
import { Rect, Circle, Transformer, Group } from "react-konva";

/**
 * ResizableElement - Wrapper that adds resize handles to any element
 * Supports: 8 handles (corners + midpoints), min/max constraints, aspect ratio lock
 */
export function ResizableElement({
  element,
  children,
  isSelected,
  onSelect,
  onDragEnd,
  onTransform,
  minWidth = 20,
  minHeight = 20,
  keepRatio = false,
}) {
  const shapeRef = useRef();
  const transformerRef = useRef();

  useEffect(() => {
    if (isSelected && transformerRef.current && shapeRef.current) {
      // Attach transformer to the element
      transformerRef.current.nodes([shapeRef.current]);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);

  const handleDragEnd = (e) => {
    onDragEnd({
      ...element,
      x: e.target.x(),
      y: e.target.y(),
    });
  };

  const handleTransformEnd = () => {
    const node = shapeRef.current;
    if (!node) return;

    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    // Reset scale and update dimensions
    node.scaleX(1);
    node.scaleY(1);

    const newAttrs = {
      ...element,
      x: node.x(),
      y: node.y(),
    };

    // Handle different element types
    if (element.type === "circle") {
      newAttrs.radius = Math.max(minWidth / 2, element.radius * Math.max(scaleX, scaleY));
    } else {
      newAttrs.width = Math.max(minWidth, (element.width || 100) * scaleX);
      newAttrs.height = Math.max(minHeight, (element.height || 100) * scaleY);
    }

    onTransform(newAttrs);
  };

  return (
    <>
      <Group
        ref={shapeRef}
        x={element.x}
        y={element.y}
        draggable={!element.locked}
        onClick={onSelect}
        onTap={onSelect}
        onDragEnd={handleDragEnd}
        onTransformEnd={handleTransformEnd}
      >
        {children}
      </Group>
      
      {isSelected && !element.locked && (
        <Transformer
          ref={transformerRef}
          flipEnabled={false}
          boundBoxFunc={(oldBox, newBox) => {
            // Limit resize
            if (Math.abs(newBox.width) < minWidth || Math.abs(newBox.height) < minHeight) {
              return oldBox;
            }
            return newBox;
          }}
          // Enable all 8 anchors (corners + midpoints)
          enabledAnchors={[
            'top-left', 'top-center', 'top-right',
            'middle-left', 'middle-right',
            'bottom-left', 'bottom-center', 'bottom-right'
          ]}
          // Style
          anchorSize={10}
          anchorCornerRadius={2}
          anchorStroke="#6366f1"
          anchorFill="#ffffff"
          anchorStrokeWidth={2}
          borderStroke="#6366f1"
          borderStrokeWidth={2}
          borderDash={[4, 4]}
          rotateEnabled={false}
          keepRatio={keepRatio}
        />
      )}
    </>
  );
}

/**
 * TextResizable - Special resizable wrapper for text elements
 * Only allows horizontal resize (width), height is calculated from text
 * Supports double-click for inline editing
 */
export function TextResizable({
  element,
  children,
  isSelected,
  onSelect,
  onDragEnd,
  onTransform,
  onDoubleClick,
  minWidth = 50,
}) {
  const shapeRef = useRef();
  const transformerRef = useRef();
  const lastClickTimeRef = useRef(0);

  useEffect(() => {
    if (isSelected && transformerRef.current && shapeRef.current) {
      transformerRef.current.nodes([shapeRef.current]);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);

  const handleDragEnd = (e) => {
    onDragEnd({
      ...element,
      x: e.target.x(),
      y: e.target.y(),
    });
  };

  const handleTransformEnd = () => {
    const node = shapeRef.current;
    if (!node) return;

    const scaleX = node.scaleX();
    
    node.scaleX(1);
    node.scaleY(1);

    onTransform({
      ...element,
      x: node.x(),
      y: node.y(),
      width: Math.max(minWidth, (element.width || 200) * scaleX),
    });
  };

  // Handle click with double-click detection
  const handleClick = () => {
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;
    
    if (timeSinceLastClick < 400) {
      // Double click detected
      if (onDoubleClick) {
        onDoubleClick(element.id);
      }
      lastClickTimeRef.current = 0; // Reset
    } else {
      // Single click - select and record time
      onSelect();
      lastClickTimeRef.current = now;
    }
  };

  const handleDoubleClick = () => {
    if (onDoubleClick) {
      onDoubleClick(element.id);
    }
  };

  return (
    <>
      <Group
        ref={shapeRef}
        x={element.x}
        y={element.y}
        draggable={!element.locked}
        onClick={handleClick}
        onTap={handleClick}
        onDblClick={handleDoubleClick}
        onDblTap={handleDoubleClick}
        onDragEnd={handleDragEnd}
        onTransformEnd={handleTransformEnd}
      >
        {children}
      </Group>
      
      {isSelected && !element.locked && (
        <Transformer
          ref={transformerRef}
          flipEnabled={false}
          boundBoxFunc={(oldBox, newBox) => {
            if (Math.abs(newBox.width) < minWidth) {
              return oldBox;
            }
            return newBox;
          }}
          // Text only gets horizontal resize handles
          enabledAnchors={['middle-left', 'middle-right']}
          anchorSize={10}
          anchorCornerRadius={2}
          anchorStroke="#3b82f6"
          anchorFill="#ffffff"
          anchorStrokeWidth={2}
          borderStroke="#3b82f6"
          borderStrokeWidth={2}
          rotateEnabled={false}
        />
      )}
    </>
  );
}

/**
 * CircleResizable - Resizable wrapper for circles
 * Maintains aspect ratio, resizes from center
 */
export function CircleResizable({
  element,
  children,
  isSelected,
  onSelect,
  onDragEnd,
  onTransform,
  minRadius = 10,
}) {
  const shapeRef = useRef();
  const transformerRef = useRef();

  useEffect(() => {
    if (isSelected && transformerRef.current && shapeRef.current) {
      transformerRef.current.nodes([shapeRef.current]);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);

  const handleDragEnd = (e) => {
    onDragEnd({
      ...element,
      x: e.target.x() - (element.radius || 40),
      y: e.target.y() - (element.radius || 40),
    });
  };

  const handleTransformEnd = () => {
    const node = shapeRef.current;
    if (!node) return;

    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    const scale = Math.max(scaleX, scaleY);
    
    node.scaleX(1);
    node.scaleY(1);

    const newRadius = Math.max(minRadius, (element.radius || 40) * scale);

    onTransform({
      ...element,
      x: node.x() - newRadius,
      y: node.y() - newRadius,
      radius: newRadius,
    });
  };

  return (
    <>
      <Group
        ref={shapeRef}
        x={element.x + (element.radius || 40)}
        y={element.y + (element.radius || 40)}
        draggable={!element.locked}
        onClick={onSelect}
        onTap={onSelect}
        onDragEnd={handleDragEnd}
        onTransformEnd={handleTransformEnd}
      >
        {children}
      </Group>
      
      {isSelected && !element.locked && (
        <Transformer
          ref={transformerRef}
          flipEnabled={false}
          boundBoxFunc={(oldBox, newBox) => {
            const minSize = minRadius * 2;
            if (Math.abs(newBox.width) < minSize || Math.abs(newBox.height) < minSize) {
              return oldBox;
            }
            return newBox;
          }}
          // Circles only get corner handles for proportional resize
          enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
          anchorSize={10}
          anchorCornerRadius={2}
          anchorStroke="#f97316"
          anchorFill="#ffffff"
          anchorStrokeWidth={2}
          borderStroke="#f97316"
          borderStrokeWidth={2}
          rotateEnabled={false}
          keepRatio={true}
        />
      )}
    </>
  );
}

export default {
  ResizableElement,
  TextResizable,
  CircleResizable,
};
